import { useFlightFiltersContext } from "@/app/lib/context/FlightFiltersContext";
import { useFlightSearchDataContext } from "@/app/lib/context/FlightSearchDataContext";
import { useFlightSortContext } from "@/app/lib/context/FlightSortContext";
import { FlightTrackingEvents, rudderAnalytics } from "@/app/lib/rudderstack";
import { Header } from "@/app/ui/flight-list/Header";
import { FlightsListDrawerComponent } from "@/app/ui/flight-list/types";
import { SingleAvailableFlightOfferSkeleton } from "@/app/ui/SingleAvailableFlightOfferSkeleton";
import { some, times } from "lodash-es";
import { useEffect, useEffectEvent, useState, type ReactNode } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useTranslations } from "use-intl";
import { FlightCalculator } from "../flights/FlightCalculator";
import { Drawer, DrawerTopBar } from "../Drawer";
import { FlightsSortDrawer } from "./FlightsSortDrawer";
import { AirlinesFilterDrawer } from "./AirlinesFilterDrawer";
import { StopsFilterDrawer } from "./StopsFilterDrawer";
import { NoFilteringResults } from "../NoFilteringResults";
import {
  type FlightType,
  type FullOfferType,
} from "@/app/lib/context/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { SingleAvailableFlightV2 } from "../SingleAvailableFlightV2";
import { FlightsFilters } from "./flight-filters/FlightsFilters";
import { getDateString } from "@/app/lib/utils/dates";
import type { FlightSearchQuery } from "@/app/lib/hooks/flightSearch/types";
import { ErrorPanel } from "../ErrorPanel";
import { IconAirplaneCrossed } from "@/app/icons";
import { Button } from "@mui/material";
import { SearchResultsWhatsAppBanner } from "../WhatsAppBanners";
import { SwipeableDrawer } from "../SwipeableDrawer";
import { useFlightSearchForm } from "@/app/lib/hooks/flightSearch/useFlightSearchForm";
import type {
  FlightSearchType,
  TFlightFormValues,
} from "@/app/lib/types/types";

type Props = {
  showHeader?: boolean;
  title?: ReactNode;
  searchQuery: { query: FlightSearchQuery; type: FlightSearchType | null };
  direction: "departure" | "return";
  onFlightSelect: (
    offer: FullOfferType,
    flight: FlightType,
    queryType: FlightSearchType | null,
  ) => void;
  isLoadingAdditionalData?: boolean;
  onAfterSearchSubmit?: () => void;
};

export const FlightsList = ({
  showHeader = true,
  title,
  searchQuery,
  direction,
  onFlightSelect,
  isLoadingAdditionalData = false,
  onAfterSearchSubmit,
}: Props) => {
  const { sortValue, updateSortValue } = useFlightSortContext();
  const { defaultFilters, filters, activeFilters, updateFilters } =
    useFlightFiltersContext();
  const areFiltersDirty = some(activeFilters, Boolean);

  const [drawerComponent, setDrawerComponent] =
    useState<FlightsListDrawerComponent | null>(null);
  const t = useTranslations();

  const { searchData } = useFlightSearchDataContext();
  const { handleSubmit } = useFlightSearchForm();

  const {
    isError,
    hasReachedEnd,
    fetchNextPage,
    result,
    isLoading: isSearchQueryLoading,
    isFetching,
    isEmpty,
  } = searchQuery.query;

  const sendNoFlightResultsFoundEvent = useEffectEvent(
    (source: FlightSearchType) => {
      if (areFiltersDirty || !searchData?.from || !searchData?.to) return;

      const travellersCount =
        searchData.adult + searchData.child + searchData.infantWithoutSeat;

      rudderAnalytics.track(FlightTrackingEvents.NO_FLIGHT_RESULTS_FOUND, {
        originId: searchData.from.id,
        originName: searchData.from.name,
        destinationId: searchData.to.id,
        destinationName: searchData.to.name,
        dateFrom: getDateString(searchData.dates.departure),
        dateTo: searchData.dates.return
          ? getDateString(searchData.dates.return)
          : undefined,
        travellers: travellersCount,
        cabinClass: searchData.cabinClass,
        source,
      });
    },
  );

  const handleSearchSubmit = (values: TFlightFormValues) => {
    handleSubmit(values);
    setDrawerComponent(null);
    if (onAfterSearchSubmit) onAfterSearchSubmit();
  };

  useEffect(() => {
    if (searchQuery.query.isSuccess && isEmpty && searchQuery.type) {
      sendNoFlightResultsFoundEvent(searchQuery.type);
    }
  }, [isEmpty, searchQuery.query.isSuccess, searchQuery.type]);

  const isLoading = (!result || isEmpty) && isSearchQueryLoading;

  const [sentryRef] = useInfiniteScroll({
    loading: isFetching,
    hasNextPage: !hasReachedEnd,
    onLoadMore: () => {
      rudderAnalytics.track(FlightTrackingEvents.USER_SEARCHED_FOR_MORE_OFFERS);
      fetchNextPage();
    },
    disabled: isError || isLoadingAdditionalData,
    rootMargin: "0px 0px 40px 0px",
  });

  return (
    <div className="flex grow flex-col px-4">
      {showHeader ? (
        <Header
          onFilterButtonClicked={setDrawerComponent}
          selectedSortValue={sortValue}
          isLoading={isSearchQueryLoading || isLoadingAdditionalData}
          activeFilters={activeFilters}
          onSearchDrawerOpen={() =>
            setDrawerComponent(FlightsListDrawerComponent.SEARCH)
          }
        />
      ) : null}

      {!isLoading && isEmpty ? (
        <div className="mt-12 flex grow flex-col items-center justify-between gap-8">
          {areFiltersDirty ? (
            <NoFilteringResults
              scope="flights"
              title={t("flights.noFlightsMatchFilters")}
              onClearFilterClick={() => updateFilters(defaultFilters)}
            />
          ) : (
            <ErrorPanel
              icon={IconAirplaneCrossed}
              primaryText={t("flights.emptyState")}
              secondaryText={t("flights.emptyStateDesc")}
            >
              <Button
                onClick={(e) => {
                  setDrawerComponent(FlightsListDrawerComponent.SEARCH);
                  e.currentTarget.blur();
                }}
                variant="outlined"
                color="primary"
                size="small"
                sx={{ borderRadius: "var(--radius-2xl)" }}
              >
                {t("editSearch")}
              </Button>
            </ErrorPanel>
          )}

          <SearchResultsWhatsAppBanner />
        </div>
      ) : (
        <div className="mt-4 w-full">
          <ul className="space-y-2" aria-label={t("flights.availableFlights")}>
            {isLoading ? (
              times(3, (index) => (
                <li key={index}>
                  <SingleAvailableFlightOfferSkeleton />
                </li>
              ))
            ) : (
              <>
                {title}
                {result?.map((offer) =>
                  offer.flightsOverview.map((fo) => {
                    const flight =
                      direction === "departure"
                        ? fo.departureFlight
                        : fo.returnFlight;

                    if (!flight) return null;

                    return (
                      <li
                        key={`${offer.index}-${flight.key}-${fo.itineraryIndex}`}
                      >
                        <SingleAvailableFlightV2
                          flight={flight}
                          direction={direction}
                          offer={offer}
                          onClick={() =>
                            onFlightSelect(offer, flight, searchQuery.type)
                          }
                        />
                      </li>
                    );
                  }),
                )}
                {isFetching || !hasReachedEnd || isLoadingAdditionalData
                  ? times(3, (index) => (
                      <li
                        key={index}
                        ref={index === 0 ? sentryRef : undefined}
                        aria-hidden={!isFetching}
                      >
                        <SingleAvailableFlightOfferSkeleton />
                      </li>
                    ))
                  : null}
              </>
            )}
          </ul>
        </div>
      )}

      <StopsFilterDrawer
        open={drawerComponent === FlightsListDrawerComponent.STOPS}
        defaultValue={defaultFilters?.stops}
        value={filters?.stops}
        onSubmit={(newStopsValue) => {
          if (!newStopsValue) return;
          updateFilters((prevFilters) =>
            prevFilters ? { ...prevFilters, stops: newStopsValue } : null,
          );
          setDrawerComponent(null);
        }}
        onClose={() => setDrawerComponent(null)}
      />

      <AirlinesFilterDrawer
        open={drawerComponent === FlightsListDrawerComponent.AIRLINES}
        value={filters?.selectedAirlines}
        defaultFilter={defaultFilters?.selectedAirlines}
        allAirlines={filters?.airlines}
        onSubmit={(newAirlinesValue) => {
          updateFilters((prevFilters) =>
            prevFilters
              ? { ...prevFilters, selectedAirlines: newAirlinesValue }
              : null,
          );
          setDrawerComponent(null);
        }}
        onCloseClick={() => setDrawerComponent(null)}
      />

      <FlightsSortDrawer
        open={drawerComponent === FlightsListDrawerComponent.SORT}
        value={sortValue}
        onSubmit={(newSort) => {
          updateSortValue(newSort);
          setDrawerComponent(null);
        }}
        onClose={() => {
          setDrawerComponent(null);
        }}
      />

      <SwipeableDrawer
        open={drawerComponent === FlightsListDrawerComponent.ALL_FILTERS}
        onClose={() => setDrawerComponent(null)}
        hasFullHeight
      >
        {/* need to reset the state when drawer is open/closed */}
        {drawerComponent === FlightsListDrawerComponent.ALL_FILTERS &&
        filters &&
        defaultFilters ? (
          <FlightsFilters
            defaultFilters={defaultFilters}
            filters={filters}
            sortValue={sortValue}
            onFiltersChange={(newFilters, newSort) => {
              updateFilters((prevFilters) =>
                prevFilters
                  ? {
                      ...prevFilters,
                      ...newFilters,
                    }
                  : null,
              );
              updateSortValue(newSort);
              setDrawerComponent(null);
            }}
            onClose={() => setDrawerComponent(null)}
          />
        ) : null}
      </SwipeableDrawer>

      <Drawer
        open={drawerComponent === FlightsListDrawerComponent.SEARCH}
        onClose={() => setDrawerComponent(null)}
      >
        <DrawerTopBar
          onCloseClick={() => setDrawerComponent(null)}
          title={t("search")}
        />

        <FlightCalculator
          submitButtonContent={t("confirm")}
          showErrorsInline
          initialValues={searchData}
          onSubmit={handleSearchSubmit}
          analyticsSource="sticky"
        />
      </Drawer>
    </div>
  );
};
